"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Save, Eraser, FileText, Loader2, UploadCloud, FileTypeCorner } from "lucide-react";
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { IResEducation, IResExperiences, IResPersonal, IResumeData, ISavedResume } from "@/lib/interfaces";
import { generateResume, getActionsCost, saveNewResume } from "./actions";
import PersonalSection from "./personalSection";
import RoleSection from "./roleSection";
import WorkSection from "./workSection";
import EducationSection from "./educationSection";
import SkillsSection from "./skillsSection";
import QuickImport from "./quickImport";
import Link from "next/link";
import LoginRedirect from "@/components/LoginRedirect";
import CartaTokens from '@/../public/images/tokens_sm.png';
import Image from 'next/image';
import AdSterraIFrameBannerAuto from "@/components/adsterra-ui/adsterra-ifram-auto";
import AdSterraNativeBanner from "@/components/adsterra-ui/adsterra-native-banner";
import Layouts from "./layouts.json";
import { executeLayoutFromFile } from "./layouts";
import { cn } from "@/lib/utils";
import { saveAs } from 'file-saver';

function ResumeGeneratorWrapper() {
  // 1. Unified State for All Details
  const [personal, setPersonal] = useState<IResPersonal>({ name: "", email: "", mobile: "", brief: "" });
  const [ role, setRole ] = useState<string>("");
  const [ experiences, setExperiences ] = useState<IResExperiences[]>([{ company: "", role: "", fromDate: new Date(), toDate: new Date(), responsibilities: "" }]);
  const [ education, setEducation ] = useState<IResEducation[]>([{ institute: "", degree: "", year: "" }]);
  const [ skills, setSkills ] = useState<string[]>([]);
  const [ isMyLoading, setIsLoading ] = useState(false);
  const [ userId, setUserId ] = useState<string>('');
  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
  const [ genPdf, setGenPdf ] = useState<string>('');
  const [ polishCost, setPolishCost ] = useState<number>(0);
  const [ isLoadingCost, setIsLoadingCost ] = useState<boolean>(false);
  const [ selectedLayout, setSelectedLayout ] = useState<number>(0);
  const [ selectedLayoutFunc, setSelectedLayoutFunc ] = useState<string>(Layouts[0].file);
  const [ isGeneratingPDF, setIsGeneratingPDF ] = useState<boolean>(false);
  const [ isGeneratingDOCX, setIsGeneratingDOCX ] = useState<boolean>(false);

  useEffect(() => {
    async function getActions () {
      setIsLoadingCost(true);

      let a = await getActionsCost('RESPRS01');
      console.log(a.data);
      setPolishCost(a.data);
      setIsLoadingCost(false);
    }
    getActions();

  }, []);

  const polishEverything = async (resumeData: IResumeData) => {
    setIsLoading(true);

    const resData = await generateResume(resumeData);

    // force user tokens balance component to refresh
		window.dispatchEvent(new Event('tokens-deducted'));
        
    if (resData.status === 200) {
      
      // Create DOCX file
      // const docxBlob = await generateDocx(resData, "1");
      // const docxUrl = window.URL.createObjectURL(docxBlob as Blob);
        
      // const docxLink = document.createElement("a");
      // docxLink.href = docxUrl;
      // docxLink.download = `Resume_${resumeData.personal.name}.docx`;
      // document.body.appendChild(docxLink);
      // docxLink.click();
        
      // document.body.removeChild(docxLink);
      // window.URL.revokeObjectURL(docxUrl);
    
    
      // Create and download .PDF file
      const pdfBlob = await executeLayoutFromFile(Layouts[selectedLayout].function, Layouts[selectedLayout].file, resData);
      // const pdfBlob = await generateModernSplitPanelLayout(resData);

      // Use a service like Cloudmersive or ConvertAPI
      const formData = new FormData();
      //   // formData.append("file", new Blob([docxBuffer]), "resume.docx");
      formData.append("file", pdfBlob as Blob, "resume.docx");
      
      const response = await fetch("https://api.cloudmersive.com/convert/docx/to/pdf", {
        method: "POST",
        headers: { "Apikey": "cc777196-8e4e-44bc-a894-89f6eb150def" },
        body: formData
      });
    
      if (!response.ok) {
        const errorText = await response.text();
        // throw new Error(`Cloudmersive Error: ${errorText}`);
        console.error(`Cloudmersive Error: ${errorText}`);
      }

      // Return as Uint8Array for Next.js response compatibility
      const pdfArrayBuffer = await response.arrayBuffer();
      const pdfUint8Array = new Uint8Array(pdfArrayBuffer);
      setGenPdf(JSON.stringify(pdfUint8Array));

      // const url2 = window.URL.createObjectURL(blob2);
      const pdfBlobOut = new Blob([pdfUint8Array], { type: "application/pdf" });
      const pdfUrl = window.URL.createObjectURL(pdfBlobOut);
      window.open(pdfUrl, '_blank');

      //2. Update all states at once with polished content
      setPersonal(prev => ({ ...prev, brief: resData.polishedData.brief }));
      setExperiences(resData.polishedData.experiences);
      setEducation(resData.polishedData.education);
      setSkills(resData.polishedData.skills);
    }
    setIsLoading(false);
  };

  const generatePDF = async (resumeData: IResumeData) => {
    setIsGeneratingPDF(true);

    try {
      // 1. Get your DOCX blob from the layout generator
      const pdfBlob = await executeLayoutFromFile(
        Layouts[selectedLayout].function,
        Layouts[selectedLayout].file,
        { polishedData: { personal, role, experiences, education, skills } }
      );

      // 2. Prepare Form Data
      const formData = new FormData();
      formData.append("file", pdfBlob as Blob, "resume.docx");

      // 3. Call Cloudmersive (NOTICE: No Content-Type header!)
      const response = await fetch("https://api.cloudmersive.com/convert/docx/to/pdf", {
        method: "POST",
        headers: { "Apikey": "cc777196-8e4e-44bc-a894-89f6eb150def" },
        body: formData,
      });

      if (!response.ok) throw new Error(await response.text());

      // 4. Trigger a "Forced Download" instead of opening in a new tab
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      
      link.href = url;
      link.setAttribute("download", "My_Resume.pdf"); // Forces the download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("PDF Generation Failed:", error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const generateDOCXF = async (resumeData: IResumeData) => {
    setIsGeneratingDOCX(true);

    const resData = { 
      polishedData: { personal, role, experiences, education, skills }
    };
            
    // Create and download .PDF file
    const docBlob = await executeLayoutFromFile(Layouts[selectedLayout].function, Layouts[selectedLayout].file, resData);
    // const pdfBlob = await generateModernSplitPanelLayout(resData);

    try {
      saveAs(docBlob, "MyResume.docx");

    } catch(error) {
      console.log(`Saving failed. ${(error as Error).message}`);
    }
    
    setIsGeneratingDOCX(false);
  };

  // --- Final Data Collection ---
  const handleGeneratePDF = async () => {
    const resumeData: IResumeData = { personal, role, experiences, education, skills };
    await generatePDF(resumeData);
  };

  const handleGenerateDOCX = async () => {
    const resumeData: IResumeData = { personal, role, experiences, education, skills };
    generateDOCXF(resumeData);
  }

  // --- Final Data Collection ---
  const handleAIPolish = async () => {
    const resumeData: IResumeData = { personal, role, experiences, education, skills };
    await polishEverything(resumeData);
  };

  const handleSave = async () => {
    const resumeData: IResumeData = { personal, role, experiences, education, skills };
    const resumeDataStringified = JSON.stringify(resumeData);

    const desData: ISavedResume = {
      userId: userId!,
      resumeElements: resumeDataStringified,
      resumeGenerated: genPdf
    }

    // const response = await res.json();
    const response = await saveNewResume(desData);    
    console.log(response);
  }

  return (
    <>
      <div className="space-y-8 max-w-7xl mx-auto">
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Resume Generator"}</span>
        </div>
        
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Resume Creator
        </h1>

        <AdSterraIFrameBannerAuto position="top" />
        
        <p className="text-gray-600 dark:text-gray-400">
            Input your context below and let  Carta-AI do the magic for you.
        </p>

        <section className="p-3 bg-white border-2 border-gray-200 rounded-xl flex flex-col justify-between overflow-x-auto scrollbar-thin">
          <h2 className="text-xl text-indigo-600 font-medium mb-4">
            Select the Resume Layout
          </h2>
          <div className="w-full flex flex-row items-center justify-between">
            {
              Layouts.map((l: any) => {
                return (
                  <div className={cn("w-[175px] shrink-0 p-3 bg-white border-2 rounded-xl text-xs transition-all select-none cursor-pointer flex flex-col gap-3 justify-between items-center", 
                    selectedLayout == l.id ? "border-indigo-500 border-2" : "border-gray-200")} 
                    key={l.id} 
                    onClick={() => {
                      setSelectedLayout(l.id);
                      setSelectedLayoutFunc(l.file);
                      console.log(l.id);
                    }}
                  >
                    {l.image && (
                      <div className="relative w-[150px] h-[200px] p-1 shrink-0 rounded-md overflow-hidden bg-gray-50 border border-gray-100">
                        <Image 
                          src={l.image} 
                          alt={`${l.shortName} preview`}
                          fill
                          className="object-contain"
                          sizes="75px"
                        />
                      </div>
                    )} 
                    {l.shortName}
                  </div>
                )
              })
            }
          </div>
        </section>
        
        {/* NEW: QUICK IMPORT COMPONENT */}
        <QuickImport setPersonal={setPersonal} setRole={setRole} 
          setExperiences={setExperiences} setEducation={setEducation} 
          setSkills={setSkills} />

        {/* PERSONAL DETAILS */}
        <PersonalSection personal={personal} setPersonal={setPersonal} />

        {/* ROLE DETAILS */}
        <RoleSection role={role} setRole={setRole} />

        {/* WORK EXPERIENCE */}
        <WorkSection experiences={experiences} setExperiences={setExperiences} />

        {/* EDUCATION BACKGROUND */}
        <EducationSection education={education} setEducation={setEducation} />

        {/* SKILLS */}
        <SkillsSection skills={skills} setSkills={setSkills} />

        {/* ACTION FOOTER */}
        <div className="flex flex-col">
          <div className="w-full flex flex-col lg:flex-row gap-4 pt-10 border-t ">
            <Button variant="destructive" className="flex-1" onClick={() => window.location.reload()}><Eraser className="mr-2 w-4 h-4" /> Clear All</Button>
            <Button variant="outline" className="flex-1" onClick={handleAIPolish}>
              { isMyLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Polishing Resume...</>
                ) : (
                  <>                  
                    <div className="flex justify-center items-center gap-2">
                      <div className="flex justify-center items-center">
                        <Sparkles className="mr-2 w-4 h-4" /> 
                        Polish Resume
                      </div>
                      <div className="ml-2 flex justify-center items-center text-xs">
                        <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                        <span className="ml-1">{polishCost} <span >tokens</span></span><span>{")"}</span> {/* className="hidden md:inline" */}
                      </div>
                    </div>
                  </>
                )}
            </Button>
            <Button className="flex-1" 
              onClick={handleSave}>
                <Save className="mr-2 w-4 h-4" /> Save Resume
            </Button>
          </div>
          <div className="w-full flex flex-col lg:flex-row gap-4 pt-4 border-t ">
            <Button variant="outline" className="flex-1 bg-indigo-500 text-white" onClick={handleGeneratePDF}>
              {/* <Sparkles className="mr-2 w-4 h-4" />  */}
              { isGeneratingPDF ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Resume...</>
                ) : (
                  <><FileText className="mr-2 w-4 h-4" /> Generate PDF</>
                )}
            </Button>
            <Button variant="outline" className="flex-1 bg-indigo-500 text-white" onClick={handleGenerateDOCX}>
              {/* <Sparkles className="mr-2 w-4 h-4" />  */}
              { isGeneratingDOCX ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Resume...</>
                ) : (
                  <><FileTypeCorner className="mr-2 w-4 h-4" /> Generate DOCX</>
                )}
            </Button>
          </div>
        </div>
      </div>

      <div className="w-full flex justify-center items-center mt-8" >
        <AdSterraNativeBanner />
      </div>

      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}
    </>
  );
}

export default ResumeGeneratorWrapper